/**
 * Display helpers for materialsDatabase_hardware rows and price-review diffs.
 * Diameter × length (Ø × L) is the primary fastener identity; SKU is secondary.
 * Ø × L is only for category Fasteners — never Decking / Post Bases / Pegs / Other.
 */

export type MaterialsCatalogDomain = 'timber' | 'glulam' | 'hardware';

export interface HardwareDiameterLength {
  diameter: number;
  length: number;
  /** Compact display e.g. "8 × 120" or `1/4" × 11 1/16` (no Ø prefix). */
  label: string;
  /** Primary label with diameter mark e.g. "Ø8 × 120" or `Ø1/4" × 11 1/16`. */
  labelWithMark: string;
}

/** Inch / mixed measure: `11 1/16`, `7/8`, `0.32`, `9`. */
const MEASURE_SRC =
  '(?:\\d+\\s+\\d+\\/\\d+|\\d+\\/\\d+|\\d+(?:\\.\\d+)?)';

/**
 * Imperial / catalog pairs: optional Ø, diameter, optional ", optional (#gauge),
 * ×/x/X, length, optional ".
 * Covers Rothoblaas strings like:
 *   `HBS WOOD SCREW Ø0.24" (#14) X 11 1/16"`
 *   `WASHER-HEAD SCREW 0.32" × 7 7/8"`
 */
const IMPERIAL_PAIR_RE = new RegExp(
  `(?:[Øø⌀]\\s*)?(${MEASURE_SRC})\\s*"?\\s*(?:\\([^)]*\\))?\\s*[xX×]\\s*(${MEASURE_SRC})\\s*"?`,
  'g',
);

/** Compact metric pairs in names (e.g. "8x120", "11 X 300") — no inch marks. */
const METRIC_PAIR_RE = /(\d+(?:\.\d+)?)\s*[xX×]\s*(\d+(?:\.\d+)?)/g;

type ParsedMeasure = {
  value: number;
  /** Original fraction / decimal token when present. */
  raw: string;
  hasFraction: boolean;
};

type DimCandidate = {
  diameter: number;
  length: number;
  score: number;
  imperial: boolean;
  diameterRaw: string;
  lengthRaw: string;
  lengthHasFraction: boolean;
};

/**
 * True when hardware category is Fasteners (case / spacing insensitive).
 * Only Fasteners should populate or display Ø × L.
 */
export function isHardwareFastenerCategory(
  category: string | null | undefined,
): boolean {
  const n = (category || '').trim().toLowerCase().replace(/\s+/g, ' ');
  return n === 'fasteners' || n === 'fastener';
}

/** Parse a single measure token (`0.32`, `11 1/16`, `7/8`) to a number. */
export function parseHardwareMeasureToken(
  raw: string | null | undefined,
): ParsedMeasure | null {
  const s = (raw || '').trim();
  if (!s) return null;

  const mixed = s.match(/^(\d+)\s+(\d+)\s*\/\s*(\d+)$/);
  if (mixed) {
    const whole = Number(mixed[1]);
    const num = Number(mixed[2]);
    const den = Number(mixed[3]);
    if (!Number.isFinite(whole) || !Number.isFinite(num) || !Number.isFinite(den) || den === 0) {
      return null;
    }
    return { value: whole + num / den, raw: s, hasFraction: true };
  }

  const frac = s.match(/^(\d+)\s*\/\s*(\d+)$/);
  if (frac) {
    const num = Number(frac[1]);
    const den = Number(frac[2]);
    if (!Number.isFinite(num) || !Number.isFinite(den) || den === 0) return null;
    return { value: num / den, raw: s, hasFraction: true };
  }

  const n = Number(s);
  if (!Number.isFinite(n) || n <= 0) return null;
  return { value: n, raw: s, hasFraction: false };
}

/**
 * Round a positive inch value to the nearest 1/16 and format as a reduced fraction.
 * Examples: 0.24 → `1/4`, 0.32 → `5/16`, 11.0625 → `11 1/16`.
 */
export function formatInchesToNearestSixteenth(inches: number): string {
  if (!Number.isFinite(inches) || inches <= 0) return String(inches);
  const rounded = Math.round(inches * 16) / 16;
  const whole = Math.floor(rounded + 1e-9);
  const frac = rounded - whole;
  if (frac < 1 / 32) return String(whole);

  let num = Math.round(frac * 16);
  let den = 16;
  if (num === 16) return String(whole + 1);
  while (num % 2 === 0 && den % 2 === 0) {
    num /= 2;
    den /= 2;
  }
  return whole > 0 ? `${whole} ${num}/${den}` : `${num}/${den}`;
}

function looksImperialDiameter(diameter: number, imperialHint: boolean): boolean {
  if (imperialHint) return true;
  // Sub-2" diameters with modest lengths are inch fasteners, not mm.
  return diameter > 0 && diameter < 2;
}

function formatDiameterLabel(diameter: number, imperial: boolean): string {
  if (imperial || looksImperialDiameter(diameter, false)) {
    return `${formatInchesToNearestSixteenth(diameter)}"`;
  }
  return formatDimNum(diameter);
}

function formatLengthLabel(
  length: number,
  imperial: boolean,
  lengthRaw: string,
  lengthHasFraction: boolean,
): string {
  if (lengthHasFraction && lengthRaw) {
    // Keep catalog fraction strings as-is (e.g. `11 1/16`, `7 7/8`).
    return lengthRaw.replace(/\s+/g, ' ').trim();
  }
  if (imperial) {
    // Decimal inches → nearest 1/16 fraction (no trailing " — matches Ø×L examples).
    return formatInchesToNearestSixteenth(length);
  }
  return formatDimNum(length);
}

function buildLabels(c: DimCandidate): Pick<HardwareDiameterLength, 'label' | 'labelWithMark'> {
  const imperial = looksImperialDiameter(c.diameter, c.imperial);
  const d = formatDiameterLabel(c.diameter, imperial);
  const l = formatLengthLabel(c.length, imperial, c.lengthRaw, c.lengthHasFraction);
  const label = `${d} × ${l}`;
  return { label, labelWithMark: `Ø${label}` };
}

function scorePair(a: number, b: number): number {
  let score = 0;
  // Prefer diameter-like first number and longer second.
  if (a <= 20 && b >= a) score += 3;
  if (b > a) score += 1;
  if (a < b) score += 1;
  // Boost inch-scale fastener pairs (decimal Ø × few–teen inch lengths).
  if (a > 0 && a < 2 && b >= 1 && b <= 24) score += 4;
  return score;
}

function collectImperialCandidates(s: string, out: DimCandidate[]): void {
  IMPERIAL_PAIR_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = IMPERIAL_PAIR_RE.exec(s)) !== null) {
    const dia = parseHardwareMeasureToken(m[1]);
    const len = parseHardwareMeasureToken(m[2]);
    if (!dia || !len) continue;
    const matched = m[0];
    const imperial =
      /["”]/.test(matched)
      || dia.hasFraction
      || len.hasFraction
      || looksImperialDiameter(dia.value, false);
    out.push({
      diameter: dia.value,
      length: len.value,
      score: scorePair(dia.value, len.value) + (imperial ? 2 : 0),
      imperial,
      diameterRaw: dia.raw,
      lengthRaw: len.raw,
      lengthHasFraction: len.hasFraction,
    });
  }
}

function collectMetricCandidates(s: string, out: DimCandidate[]): void {
  // Skip when the string already has imperial inch marks — imperial regex owns those.
  if (/["”]/.test(s) || /[Øø⌀]/.test(s)) return;
  METRIC_PAIR_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = METRIC_PAIR_RE.exec(s)) !== null) {
    const a = Number(m[1]);
    const b = Number(m[2]);
    if (!Number.isFinite(a) || !Number.isFinite(b) || a <= 0 || b <= 0) continue;
    out.push({
      diameter: a,
      length: b,
      score: scorePair(a, b),
      imperial: false,
      diameterRaw: m[1],
      lengthRaw: m[2],
      lengthHasFraction: false,
    });
  }
}

/**
 * Parse Ø × L from name, description, or free text.
 * Prefers pairs where the first number looks like a fastener diameter (smaller)
 * and the second like a length.
 *
 * Handles Rothoblaas imperial forms (Ø prefix, `"`, ×/X, fractions) and
 * metric catalog codes (`9X100`). Bits / length-only strings without a
 * diameter×length pair return null (no invented Ø).
 *
 * Callers that render the catalog column must use
 * {@link resolveHardwareDiameterLengthForDisplay} so non-Fasteners stay blank.
 */
export function parseHardwareDiameterLength(
  ...texts: Array<string | null | undefined>
): HardwareDiameterLength | null {
  const candidates: DimCandidate[] = [];
  for (const raw of texts) {
    const s = (raw || '').trim();
    if (!s) continue;
    collectImperialCandidates(s, candidates);
    collectMetricCandidates(s, candidates);
  }
  if (!candidates.length) return null;
  candidates.sort((x, y) => y.score - x.score || x.diameter - y.diameter);
  const best = candidates[0];
  const labels = buildLabels(best);
  return {
    diameter: best.diameter,
    length: best.length,
    label: labels.label,
    labelWithMark: labels.labelWithMark,
  };
}

/**
 * Ø × L for catalog / review display: only Fasteners; other categories → null (show —).
 * Optional dedicated diameter/length numbers win over text parse when both are finite.
 */
export function resolveHardwareDiameterLengthForDisplay(
  category: string | null | undefined,
  ...textsOrFields: Array<string | number | null | undefined>
): HardwareDiameterLength | null {
  if (!isHardwareFastenerCategory(category)) return null;

  let diameterField: number | null = null;
  let lengthField: number | null = null;
  const texts: Array<string | null | undefined> = [];
  for (const v of textsOrFields) {
    if (typeof v === 'number') {
      if (Number.isFinite(v) && v > 0) {
        if (diameterField == null) diameterField = v;
        else if (lengthField == null) lengthField = v;
      }
      continue;
    }
    texts.push(v);
  }

  if (diameterField != null && lengthField != null) {
    // Numeric fields: treat sub-2 values as inches; larger as metric mm.
    const imperial = looksImperialDiameter(diameterField, false);
    const labels = buildLabels({
      diameter: diameterField,
      length: lengthField,
      score: 0,
      imperial,
      diameterRaw: String(diameterField),
      lengthRaw: String(lengthField),
      lengthHasFraction: false,
    });
    return {
      diameter: diameterField,
      length: lengthField,
      label: labels.label,
      labelWithMark: labels.labelWithMark,
    };
  }

  return parseHardwareDiameterLength(...texts);
}

function formatDimNum(n: number): string {
  return Number.isInteger(n) ? String(n) : String(n);
}

export function formatHardwareDiameterLengthLabel(
  dims: HardwareDiameterLength | null | undefined,
  fallback = '—',
): string {
  return dims?.labelWithMark || fallback;
}

/** Active unit price from top-level unitPrice or supplierPricing. */
export function resolveHardwareUnitPrice(item: {
  unitPrice?: number | null;
  supplier?: string | null;
  supplierPricing?: Array<{ supplier?: string | null; unitPrice?: number | null }> | null;
}): number | null {
  const top = typeof item.unitPrice === 'number' && Number.isFinite(item.unitPrice)
    ? item.unitPrice
    : null;
  if (top != null && top > 0) return top;

  const pricing = Array.isArray(item.supplierPricing) ? item.supplierPricing : [];
  const want = (item.supplier || '').trim().toLowerCase();
  if (want) {
    const match = pricing.find(
      (sp) => (sp.supplier || '').trim().toLowerCase() === want,
    );
    const p = match && typeof match.unitPrice === 'number' && Number.isFinite(match.unitPrice)
      ? match.unitPrice
      : null;
    if (p != null && p > 0) return p;
  }

  for (const sp of pricing) {
    const p = typeof sp.unitPrice === 'number' && Number.isFinite(sp.unitPrice) ? sp.unitPrice : null;
    if (p != null && p > 0) return p;
  }

  // Explicit zero is a real price; missing/NaN is not.
  if (top === 0) return 0;
  return null;
}

/** Normalize alternate agent field names onto the hardware diff shape. */
export function normalizeHardwareUpdateFields(
  raw: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...raw };

  if (out.unitPrice == null) {
    const alt =
      raw.pricePerUnit ?? raw.price_per_unit ?? raw.unit_price ?? raw.price ?? raw.Price;
    const n = typeof alt === 'number' ? alt : typeof alt === 'string' ? Number(alt) : NaN;
    if (Number.isFinite(n) && n >= 0) out.unitPrice = n;
  }

  if (out.existingUnitPrice == null) {
    const alt = raw.existingPrice ?? raw.existing_unit_price;
    const n = typeof alt === 'number' ? alt : typeof alt === 'string' ? Number(alt) : NaN;
    if (Number.isFinite(n) && n >= 0) out.existingUnitPrice = n;
  }

  if (!out.sku || (typeof out.sku === 'string' && !out.sku.trim())) {
    const alt = raw.item ?? raw.code ?? raw.productCode ?? raw.product_code ?? raw.Item;
    if (typeof alt === 'string' && alt.trim()) out.sku = alt.trim();
  }

  if (!out.name || (typeof out.name === 'string' && !out.name.trim())) {
    const alt = raw.description ?? raw.Description ?? raw.title;
    if (typeof alt === 'string' && alt.trim()) out.name = alt.trim();
  }

  if (!out.unit || (typeof out.unit === 'string' && !out.unit.trim())) {
    const alt = raw.uom ?? raw.Unit;
    if (typeof alt === 'string' && alt.trim()) {
      const u = alt.trim().toLowerCase();
      out.unit = u === 'pcs' || u === 'pc' || u === 'ea' ? 'each' : alt.trim();
    }
  }

  // Script used action: 'create' — treat as 'new' for the review UI.
  if (out.action === 'create') out.action = 'new';

  return out;
}

function updateLooksLikeHardware(u: Record<string, unknown>): boolean {
  const hasUnitPrice =
    (typeof u.unitPrice === 'number' && Number.isFinite(u.unitPrice))
    || (typeof u.existingUnitPrice === 'number' && Number.isFinite(u.existingUnitPrice));
  const hasSku = typeof u.sku === 'string' && u.sku.trim().length > 0;
  const hasName = typeof u.name === 'string' && u.name.trim().length > 0;
  const hasCategory = typeof u.category === 'string' && u.category.trim().length > 0;
  const hasTimberShape =
    (typeof u.species === 'string' && u.species.trim().length > 0)
    || (typeof u.dimensions === 'string' && u.dimensions.trim().length > 0)
    || (typeof u.piecePrice === 'number' && Number.isFinite(u.piecePrice))
    || (typeof u.costPerBdFt === 'number' && Number.isFinite(u.costPerBdFt));

  if (hasTimberShape && !hasUnitPrice && !hasSku) return false;
  if (hasUnitPrice || hasSku) return true;
  if (hasCategory && hasName && !hasTimberShape) return true;
  return false;
}

const HARDWARE_SUPPLIER_HINTS = [
  'rothoblaas',
  'rotho blaas',
  'rotho-blaas',
  'fastener',
  'simpson',
  'strong-tie',
  'strongtie',
];

function supplierLooksHardware(...vals: Array<string | null | undefined>): boolean {
  const blob = vals.filter(Boolean).map((v) => String(v).toLowerCase()).join(' ');
  return HARDWARE_SUPPLIER_HINTS.some((h) => blob.includes(h));
}

/**
 * Resolve timber vs hardware for a proposal, including mis-tagged Rothoblaas
 * rows that omitted catalogDomain or wrote empty timber-shaped diffs.
 */
export function resolveProposalCatalogDomain(input: {
  catalogDomain?: string | null;
  supplierName?: string | null;
  supplierOrgName?: string | null;
  fileName?: string | null;
  parseNotes?: string | null;
  updates?: Array<Record<string, unknown>> | null;
}): MaterialsCatalogDomain {
  const raw = (input.catalogDomain || '').trim().toLowerCase();
  if (raw === 'hardware' || raw === 'fasteners' || raw === 'hardware_and_fasteners') {
    return 'hardware';
  }
  if (raw === 'glulam' || raw === 'glulams') {
    return 'glulam';
  }

  const updates = (input.updates || []).map((u) =>
    normalizeHardwareUpdateFields(u && typeof u === 'object' ? u : {}),
  );
  const hardwareish = updates.filter(updateLooksLikeHardware);
  const timberish = updates.filter((u) => {
    return (
      (typeof u.species === 'string' && u.species.trim())
      || (typeof u.piecePrice === 'number' && Number.isFinite(u.piecePrice))
      || (typeof u.costPerBdFt === 'number' && Number.isFinite(u.costPerBdFt))
    );
  });

  const glulamish = timberish.filter((u) => updateLooksLikeGlulam(u));

  if (hardwareish.length > 0 && timberish.length === 0) return 'hardware';
  if (glulamish.length > 0 && glulamish.length === timberish.length) return 'glulam';

  if (
    supplierLooksHardware(
      input.supplierName,
      input.supplierOrgName,
      input.fileName,
      input.parseNotes,
    )
    && timberish.length === 0
  ) {
    return 'hardware';
  }

  const blob = [
    input.supplierName,
    input.supplierOrgName,
    input.fileName,
    input.parseNotes,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (/\bglulam\b|\bglu-lam\b|\b24f[-\s]?/i.test(blob)) {
    return 'glulam';
  }

  if (raw === 'timber') return 'timber';
  return timberish.length > 0 ? 'timber' : raw ? 'timber' : 'timber';
}

/** Heuristic: engineered glulam beam line (24F grades, glulam species). */
export function updateLooksLikeGlulam(u: Record<string, unknown>): boolean {
  const species = String(u.species || '').toLowerCase();
  const grade = String(u.grade || '').toUpperCase();
  const dims = String(u.dimensions || '').toLowerCase();
  if (species.includes('glulam') || species.includes('glu-lam')) return true;
  if (/\b24F[-\s]?(V4|EWS|EX|V\d)/i.test(grade) || /\b20F[-\s]?/i.test(grade)) return true;
  if (dims.includes('glulam')) return true;
  return false;
}

/** Primary title for a hardware review diff: Ø×L first (Fasteners only), then name, SKU secondary. */
export function formatHardwareDiffTitle(u: {
  sku?: string | null;
  name?: string | null;
  description?: string | null;
  category?: string | null;
}): string {
  const dims = resolveHardwareDiameterLengthForDisplay(
    u.category,
    u.name,
    u.description,
    u.sku,
  );
  const name = (u.name || '').trim();
  const sku = (u.sku || '').trim();
  const parts: string[] = [];
  if (dims) parts.push(dims.labelWithMark);
  if (name && (!dims || !name.includes(`${dims.diameter}`) || !name.toLowerCase().includes('x'))) {
    // Prefer a short name when dims already encode size from the name.
    if (!dims || name.toUpperCase() !== sku.toUpperCase()) {
      parts.push(name);
    }
  } else if (name && !dims) {
    parts.push(name);
  }
  if (sku) parts.push(sku);
  if (u.category && String(u.category).trim() && !isHardwareFastenerCategory(u.category)) {
    parts.push(String(u.category).trim());
  }
  return parts.filter(Boolean).join(' · ') || 'Hardware SKU';
}
